import { CartAddEvent } from '@theme/events';
import { lockScroll, unlockScroll } from '@theme/dialog';

/* Final v6 PDP shell. Commerce remains owned by Horizon's product-form and
   purchase-state components. Accessory quick-add uses native product forms and bundled section rendering. */
/* DONE with an empty box says why, as the buy buttons do (xz-v2-ws-pdp.js engraving guard: "Enter your
   engraving, or switch engraving off." / "Enter line 2, or remove the second line."), then focuses it. */
function requestEngravingConfirmation(container, first, second, twoLines, finish, pdp) {
  const empty = !first?.value.trim() ? first : (twoLines && !second?.value.trim() ? second : null);
  if (empty) {
    if (!window.xzV2PdpEngravingGuard?.(pdp)) empty.focus();
    return;
  }
  const request = new Event('engraving:preview-request', { cancelable: true });
  if (container.dispatchEvent(request)) finish();
}

/* Phones and tablets: the gallery is a native scroll-snap track (xz-v2-ws-pdp.css); 992+: one panel at a time. */
const phoneQuery = window.matchMedia('(max-width: 991px)');
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const canGlide = () => !reducedMotion() && 'scrollBehavior' in document.documentElement.style;
/* quiet: no focus ring (focusVisible:false, ignored where unsupported): the viewer's focus return after a pointer open. */
const focusQuiet = (node, quiet) => { if (!node) return; try { node.focus(quiet ? { preventScroll: true, focusVisible: false } : { preventScroll: true }); } catch (error) { node.focus(); } };
const idle = (fn) => (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 200));
/* A stylesheet added by script (never render-blocking); resolves once it has loaded. */
const stylesheets = new Map();
const loadStylesheet = (href) => {
  if (!stylesheets.has(href)) {
    stylesheets.set(href, new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = () => resolve();
      link.onerror = () => { link.remove(); stylesheets.delete(href); reject(new Error('xz-v2-pdp: stylesheet ' + href)); };
      document.head.appendChild(link);
    }));
  }
  return stylesheets.get(href);
};
const optimizeFloat = value => window.XZV2FrameClock ? window.XZV2FrameClock.optimizeFloat(value) : (Math.abs(Math.round(value * 100000) / 100000) > 0.0001 ? Math.round(value * 100000) / 100000 : 0);
const initialHover = window.innerWidth > 991;
if (!customElements.get('xz-v2-pdp')) {
  class XinzuoPdp extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.toggleAttribute('data-v2-hover',initialHover);
      this.frames = new Map();
      this.motionClock = window.XZV2FrameClock;
      // The shared frame clock is held only while an animation runs (as xz-v2-hero.js does): holding it for the page
      // lifetime kept a 60fps rAF loop (and a style pass per frame) running on an idle PDP.
      this.clockReleases = new Map();
      this.enhanceEngraving();
      const { signal } = this.abort;
      this.initSeries(signal);
      // Source a-29/a-30 resolves the named swatches, not the stale literal RGB.
      let desktop = window.innerWidth > 991;
      const rating = this.querySelector('.xz-product-rating');
      const review = rating?.querySelector('.rev-link');
      const swatch = name => {
        const value = getComputedStyle(this).getPropertyValue(name).trim();
        const hex = value.replace('#', '');
        return parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.slice(0, 2), 16);
      };
      const resetRating = () => {
        if (!review) return;
        cancelAnimationFrame(this.frames.get(review)); this.frames.delete(review); this.releaseClock(review);
        if (desktop) { this.ratingColor = swatch('--text-col-2'); review.style.color = `rgb(${this.ratingColor}, ${this.ratingColor}, ${this.ratingColor})`; }
        else review.style.removeProperty('color');
      };
      const hoverRating = enter => {
        if (!review || window.innerWidth <= 991) return;
        const from = this.ratingColor, to = swatch(enter ? '--text-col-titles' : '--text-col-2');
        this.animateMotion(review, from, to, 300, value => { this.ratingColor = value; const c = Math.round(value); review.style.color = `rgb(${c}, ${c}, ${c})`; });
      };
      resetRating();
      rating?.addEventListener('mouseenter', () => hoverRating(true), { signal });
      rating?.addEventListener('mouseleave', () => hoverRating(false), { signal });
      window.addEventListener('resize', () => { const next = window.innerWidth > 991; if (next !== desktop) { desktop = next; resetRating(); this.revealActiveThumb(); } this.pinTrack(); }, { signal });
      /* "Complete your set" ADD is taken on the button's click with preventDefault, as on the PLP cards
         (xz-v2-collection.js), so the /cart/add form is never submitted: a submitted form is reported to
         Shopify's analytics as a second add-to-cart (form + fetch), which the A/B would count twice.
         Enter/Space on the button also fire click. A submit that still arrives (an implicit submission)
         is stopped in the capture phase and routed to the same add. */
      this.addEventListener('click', (event) => {
        if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const button = event.target.closest?.('[data-pdp-upsell] button[type=submit]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        const form = button.form || button.closest('[data-pdp-upsell]');
        if (form) this.addAccessory(form);
      }, { signal });
      this.addEventListener('submit', (event) => {
        if (!event.target.matches?.('[data-pdp-upsell]')) return;
        event.preventDefault();
        this.addAccessory(event.target);
      }, { signal, capture: true });
      this.addEventListener('click', (event) => {
        const zoom = event.target.closest('[data-pdp-zoom]');
        if (zoom) this.openZoom(zoom, event.detail > 0);   // detail 0 = Enter/Space on the button (keyboard)
        const thumb = event.target.closest('[data-pdp-media]');
        if (thumb) this.selectMedia(thumb.dataset.pdpMedia);
      }, { signal });
      this.addEventListener('keydown', (event) => {
        /* The photo's own button (the one tab stop of the gallery image): arrows / Home / End change the photo and keep
           focus on it, stopping at both ends like a swipe. One photo: no carousel, so the keys keep their page meaning
           (End scrolls the page). */
        const zoom = event.target.closest('[data-pdp-zoom]');
        if (zoom && ['ArrowLeft','ArrowRight','Home','End'].includes(event.key) && !event.altKey && !event.ctrlKey && !event.metaKey) {
          const slides = this.gallerySlides();
          const n = slides.length;
          if (n < 2) return;
          let i = this.gIndex;
          if (event.key === 'Home') i = 0;
          else if (event.key === 'End') i = n - 1;
          else i = Math.max(0, Math.min(n - 1, i + (event.key === 'ArrowRight' ? 1 : -1)));
          event.preventDefault();
          if (slides[i] && i !== this.gIndex) this.selectMedia(slides[i].dataset.mediaId, { focus: true });
          return;
        }
        const thumb = event.target.closest('[data-pdp-media]');
        if (!thumb || !['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(event.key)) return;
        const thumbs = Array.from(this.querySelectorAll('[data-pdp-media]'));
        let i = thumbs.indexOf(thumb);
        if (event.key === 'Home') i = 0;
        else if (event.key === 'End') i = thumbs.length - 1;
        else i = (i + (['ArrowRight','ArrowDown'].includes(event.key) ? 1 : -1) + thumbs.length) % thumbs.length;
        event.preventDefault();
        thumbs[i]?.focus();
        if (thumbs[i]) this.selectMedia(thumbs[i].dataset.pdpMedia);
      }, { signal });
      this.initGallery(signal);
      this.initViewerHistory(signal);
      this.querySelectorAll('[data-pdp-disclosure]').forEach((details) => {
        /* Milen a-31/a-32 turn the chevron in the click frame: start it from the summary click
           (the toggle event lands a frame later); toggle still covers keyboard and editor opens. */
        details.querySelector('summary')?.addEventListener('click', () => this.rotate(details, !details.open), { signal });
        details.addEventListener('toggle', () => this.rotate(details, details.open), { signal });
      });
      /* Warm a photo before it is chosen (hover, touch, focus on its thumbnail), so the swap has no empty frame. */
      const warm = (event) => { const thumb = event.target.closest?.('[data-pdp-media]'); if (thumb) this.mediaImage(thumb.dataset.pdpMedia, true); };
      this.addEventListener('pointerover', warm, { signal, passive: true });
      this.addEventListener('focusin', warm, { signal });
      this.closest('.shopify-section')?.addEventListener('variant:update', (event) => {
        const html = event.detail?.data?.html;
        const gallery = html?.querySelector('xz-v2-pdp [data-xz-v2-pdp-gallery]');
        const current = this.querySelector('[data-xz-v2-pdp-gallery]');
        if (gallery && current) {
          current.querySelectorAll('video').forEach((video) => video.pause());
          current.replaceWith(gallery.cloneNode(true));
          this.adoptGallery();
        }
      }, { signal });
      document.addEventListener('shopify:block:select', (event) => {
        if (!this.contains(event.target)) return;
        const details = event.target.closest('[data-pdp-disclosure]');
        if (details) details.open = true;
      }, { signal });
      /* A variant whose picture is deep in the list (knife roll Grey = 12th of 13) opens with its
         thumbnail scrolled into the rail, so the gold frame is visible. */
      this.revealActiveThumb();
      window.addEventListener('load', () => this.revealActiveThumb(), { signal, once: true });
      /* Gift card 'Send on' date: empty dd/mm/yyyy segments are placeholder grey, a chosen date white. */
      const syncDate = (input) => input.classList.toggle('xz-v2-has-value', !!input.value);
      this.querySelectorAll('.field--send-on input[type=date]').forEach(syncDate);
      this.addEventListener('input', (event) => { if (event.target.matches?.('.field--send-on input[type=date]')) syncDate(event.target); }, { signal });
      this.addEventListener('change', (event) => { if (event.target.matches?.('.field--send-on input[type=date]')) syncDate(event.target); }, { signal });
    }
    /* "More from this series" row: Milen's .ff-carousel-parent drag (mouse/pen; touch scrolls natively), a drag
       of more than 3px never follows the link it started on; arrow keys scroll it when the row has focus. */
    initSeries(signal) {
      const row = this.querySelector('[data-xz-v2-series]');
      if (!row) return;
      let down = false, moved = false, start = 0, scroll = 0, id = null;
      row.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      row.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        down = true; moved = false; start = event.clientX; scroll = row.scrollLeft; id = event.pointerId;
      }, { signal });
      row.addEventListener('pointermove', (event) => {
        if (!down || event.pointerId !== id) return;
        const dx = event.clientX - start;
        if (!moved && Math.abs(dx) > 3) { moved = true; row.classList.add('is-dragging'); try { row.setPointerCapture(id); } catch (error) {} }
        if (moved) row.scrollLeft = scroll - dx;
      }, { signal });
      const end = (event) => {
        if (!down || event.pointerId !== id) return;
        down = false;
        if (event.type === 'pointercancel') moved = false;
        if (row.hasPointerCapture?.(id)) row.releasePointerCapture(id);
        id = null;
        row.classList.remove('is-dragging');
      };
      row.addEventListener('pointerup', end, { signal });
      row.addEventListener('pointercancel', end, { signal });
      row.addEventListener('click', (event) => { if (moved) { event.preventDefault(); event.stopPropagation(); moved = false; } }, { signal, capture: true });
      row.addEventListener('keydown', (event) => {
        if (event.target !== row || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
        row.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * row.clientWidth * 0.8, behavior: reduce ? 'auto' : 'smooth' });
      }, { signal });
    }
    /* Scroll the thumbnail rail itself (never the page, so no scrollIntoView) until the active
       thumb is in view: scrollTop on the vertical rail at 992+, scrollLeft on the row below. */
    revealActiveThumb(smooth) {
      this.scrollRail(this.railTarget(this.querySelector('.ff-pdp-thumbnails .xz-pdp-thumb.ff-is-active')), smooth);
    }
    /* Reads only: where the rail has to scroll so `thumb` is in view (null = already in view). markThumbs calls it
       before its class writes, so a photo change costs at most one layout (fix r1: read after write forced a second
       style + layout of the whole PDP mid-swipe). The active class changes no box, so the reading stays valid. */
    railTarget(thumb) {
      const rail = thumb && this.querySelector('.ff-pdp-thumbnails');
      if (!rail) return null;
      const r = rail.getBoundingClientRect(), t = thumb.getBoundingClientRect();
      const vertical = getComputedStyle(rail).flexDirection.indexOf('column') === 0;
      if (vertical && rail.scrollHeight > rail.clientHeight + 1) {
        const top = t.top - r.top + rail.scrollTop;
        if (top < rail.scrollTop || top + t.height > rail.scrollTop + rail.clientHeight) return { rail, top: Math.max(0, top - (rail.clientHeight - t.height) / 2) };
      }
      if (!vertical && rail.scrollWidth > rail.clientWidth + 1) {
        const left = t.left - r.left + rail.scrollLeft;
        if (left < rail.scrollLeft || left + t.width > rail.scrollLeft + rail.clientWidth) return { rail, left: Math.max(0, left - (rail.clientWidth - t.width) / 2) };
      }
      return null;
    }
    scrollRail(target, smooth) {
      if (!target) return;
      if (target.top != null) { target.rail.scrollTop = target.top; return; }
      /* The row glides along with a swipe or a thumbnail tap (phones); load, resize and the viewer jump. */
      if (smooth && canGlide()) target.rail.scrollTo({ left: target.left, behavior: 'smooth' }); else target.rail.scrollLeft = target.left;
    }
    async addAccessory(form) {
      const button = form.querySelector('button[type=submit]');
      const status = form.querySelector('[role=status]');
      if (!button || button.disabled || button.dataset.busy === 'true') return;
      /* Busy, not disabled: disabling the focused button sends focus to <body>, so the drawer the add
         opens handed focus back to <body> on close and the next Tab jumped to the header (r4). The
         flag keeps the one-add-per-press guard (a double press adds one unit; clicks while busy reach
         the handler and stop here), aria-disabled tells screen readers, and the inline style is the
         .ff-cta:disabled busy look (xz-v2-pdp-native.css). */
      const setBusy = (busy) => {
        if (busy) { button.dataset.busy = 'true'; button.setAttribute('aria-disabled', 'true'); }
        else { delete button.dataset.busy; button.removeAttribute('aria-disabled'); }
        button.style.opacity = busy ? '.6' : '';
        button.style.cursor = busy ? 'wait' : '';
      };
      setBusy(true);
      if (status) status.textContent = '';
      const id = String(new FormData(form).get('id'));
      const ids = [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), node => node.dataset.sectionId))].slice(0,5);
      /* Same request shape as the product form and the PLP card add (JSON, no Accept header), which
         Shopify's storefront observers and the app web pixels expect. */
      const payload = { id, quantity: 1, properties: {} };
      if (ids.length) { payload.sections = ids.join(','); payload.sections_url = location.pathname; }
      let added = false;
      try {
        const endpoint = new URL(form.action, location.origin);
        endpoint.pathname = endpoint.pathname.replace(/\/add\/?$/, '/add.js');
        endpoint.search = '';
        const response = await fetch(endpoint, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'This item could not be added.');
        added = true;
        const sections = result.sections || {};
        let itemCount;
        for (const html of Object.values(sections)) {
          if (!html) continue;
          const dom = new DOMParser().parseFromString(html,'text/html');
          const text = dom.querySelector('[ref="cartItemCount"]')?.textContent;
          if (text != null) {const parsed=Number(text.trim()); if (Number.isFinite(parsed)) itemCount=parsed;}
        }
        if (itemCount == null) {
          const root = endpoint.pathname.replace(/cart\/add\.js$/, 'cart.js');
          const cartResponse = await fetch(root,{headers:{Accept:'application/json'}});
          if (!cartResponse.ok) throw new Error('Added to cart. Open your cart to review it.');
          /* Visible count: the hidden engraving-fee lines never count (xz-v2-cart-count). */
          itemCount = ((await cartResponse.json()).items || []).reduce((n, item) => ([43781283217459, 43781283250227].includes(item.variant_id) ? n : n + item.quantity), 0);
        }
        document.dispatchEvent(new CartAddEvent(result,form.id,{source:'xz-v2-pdp-upsell',variantId:id,itemCount,sections}));
        if (status) status.textContent = 'Added';
      } catch (error) {
        if (status) status.textContent = added ? 'Added to cart. Open your cart to review it.' : error.message;
      } finally {setBusy(false); button.disabled = false;}
    }
    enhanceEngraving() {
      const container = this.querySelector('.engraving-container');
      if (!container || container.querySelector('[data-v2-engraving-switch]')) return;
      const nativeAdd = container.querySelector('#btn-add-engraving');
      const nativeRemove = container.querySelector('#btn-no-engraving');
      const nativeOne = container.querySelector('#btn-one-line');
      const nativeTwo = container.querySelector('#btn-two-lines');
      const content = container.querySelector('#engrave-div');
      const state = container.querySelector('#engrave-state');
      const firstInput = container.querySelector('#engravedInput');
      const originalPlaceholder = firstInput?.getAttribute('placeholder');
      if (!nativeAdd || !nativeRemove || !nativeOne || !nativeTwo || !content || !state) return;
      if (originalPlaceholder === 'Enter text, e.g.: Chef') {
        /* Below 360px the 0/20 counter cuts 'Your name or message' ('Your name or messa'). */
        const narrow = window.matchMedia('(max-width: 359px)');
        const setPlaceholder = () => firstInput.setAttribute('placeholder', narrow.matches ? 'Your name' : 'Your name or message');
        setPlaceholder();
        if (narrow.addEventListener) narrow.addEventListener('change', setPlaceholder, { signal: this.abort.signal });
      }
      const onePrice = nativeOne.querySelector('.line-btn-price')?.textContent.trim() || '';
      const twoPrice = nativeTwo.querySelector('.line-btn-price')?.textContent.trim() || '';
      /* The switch shows the engraving the cart add will carry (product-form.js reads window.engravingSelected),
         never the hidden #engrave-state checkbox alone: Chrome can restore that box on Back while the panel and
         the window state start off (r3: tap 1 then turned the switch gold with no panel, and the add dropped
         the engraving). The panel starts hidden in the markup, so a fresh page is off. */
      const engravingOn = () => window.engravingSelected === true && state.checked;
      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'xz-v2-pdp-reset ff-content-wrp ff-is-horizontal ff-is-center xz-v2-pdp-engraving-switch';
      toggle.dataset.v2EngravingSwitch = '';
      toggle.setAttribute('role', 'switch');
      toggle.setAttribute('aria-checked', String(engravingOn() && content.style.display !== 'none'));
      toggle.setAttribute('aria-controls', content.id);
      const switchBox = document.createElement('span'); switchBox.className = 'ff-switch-wrp';
      const knob = document.createElement('span'); knob.className = 'ff-knob'; switchBox.append(knob);
      const label = document.createElement('span'); label.className = 'ff-button-1-txt';
      label.textContent = `Add Engraving (${onePrice})`;
      toggle.append(switchBox,label);
      /* Milen 2 Oct (pdp-engraving-example): the switch row also holds the "✓ ADDED" confirmation. It is the
         switch's sibling, never inside it: a tap on it reopens the bar instead of switching engraving off. */
      const row = document.createElement('div');
      row.className = 'ff-content-wrp ff-is-horizontal ff-is-center xz-v2-pdp-engraving-row';
      const confirm = document.createElement('button');
      confirm.type = 'button';
      confirm.className = 'xz-v2-pdp-reset xz-confirm-wrp';
      confirm.hidden = true;
      confirm.setAttribute('aria-label', 'Engraving added. Edit engraving');
      const confirmIcon = document.createElement('span'); confirmIcon.className = 'ff-icon ff-is-16'; confirmIcon.setAttribute('aria-hidden', 'true');
      confirmIcon.append(new DOMParser().parseFromString('<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>', 'image/svg+xml').documentElement);
      const confirmLabel = document.createElement('span'); confirmLabel.className = 'ff-button-1-txt'; confirmLabel.textContent = 'added';
      confirm.append(confirmIcon, confirmLabel);
      const live = document.createElement('span'); live.className = 'visually-hidden'; live.setAttribute('aria-live', 'polite');
      row.append(toggle, confirm, live);
      container.insertBefore(row, container.firstChild);
      const heading = document.createElement('label');
      heading.className = 'ff-headline-4'; heading.htmlFor = 'engravedInput';
      heading.textContent = 'Enter Engraving Text:'; content.prepend(heading);
      const note = content.querySelector('.engraving-note');
      const noteNext = note?.nextSibling;
      const noteOriginal = note?.innerHTML;
      const noteOriginalId = note?.getAttribute('id');
      /* Screen readers: line 2 gets its own name (the placeholder vanishes once typed into), and both
         lines are described by the visible rule under the heading and the length limit. Tokens are
         added beside any already there (the buy-button guard adds and removes its own). */
      const secondInputEl = container.querySelector('#engravedInput2');
      const secondLabelEl = document.createElement('label');
      secondLabelEl.className = 'visually-hidden'; secondLabelEl.htmlFor = 'engravedInput2'; secondLabelEl.textContent = 'Line 2';
      if (secondInputEl) secondInputEl.before(secondLabelEl);
      const limitHint = document.createElement('span');
      limitHint.className = 'visually-hidden'; limitHint.id = 'xz-v2-engraving-limit';
      limitHint.textContent = `Up to ${Number(container.dataset.maxLength) || 20} characters per line.`;
      if (note && !note.id) note.id = 'xz-v2-engraving-note';
      const describedTokens = [note?.id, limitHint.id].filter(Boolean);
      const describe = (input, add) => {
        if (!input) return;
        const tokens = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean).filter((token) => !describedTokens.includes(token));
        if (add) tokens.push(...describedTokens);
        if (tokens.length) input.setAttribute('aria-describedby', tokens.join(' ')); else input.removeAttribute('aria-describedby');
      };
      if (note) {
        heading.after(note);
        // Expand only the inherited default; preserve merchant-authored instructions.
        const currentHint = note.textContent.trim();
        const hint = currentHint === 'Letters, numbers, and basic punctuation only.'
          ? "Letters, numbers, and basic punctuation only (. , ! ? ' -)"
          : currentHint;
        if (hint.startsWith('Letters, numbers,')) {
          note.replaceChildren(document.createTextNode('Letters, numbers,'), document.createElement('br'),
            document.createTextNode(hint.slice('Letters, numbers,'.length).trim()));
        }
      }
      (note || heading).after(limitHint);
      describe(firstInput, true);
      describe(secondInputEl, true);
      const second = document.createElement('button'); second.type = 'button';
      second.className = 'xz-v2-pdp-reset ff-cta-text-btn';
      const secondIcon = document.createElement('span'); secondIcon.className = 'ff-icon ff-is-16 xz-v2-engraving-plus';secondIcon.setAttribute('aria-hidden','true');
      secondIcon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><line class="xz-v2-plus-vertical" x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>';
      const secondLabel = document.createElement('span'); secondLabel.className = 'ff-button-3-txt'; second.append(secondIcon,secondLabel);
      const amount = Number(twoPrice.replace(/[^0-9.]/g,'')) - Number(onePrice.replace(/[^0-9.]/g,''));
      const sync = () => {
        toggle.setAttribute('aria-checked', String(engravingOn()));
        const two = nativeTwo.classList.contains('selected');
        /* The row states the fee the add will charge: the two-line price once line 2 is added (r3: '+$19 ✓ ADDED'
           on a $29 engraving). One line keeps Milen's exact label (the first-paint --xz-v2-engraving-label). */
        label.textContent = `Add Engraving (${two && twoPrice ? twoPrice : onePrice})`;
        /* The phone keyboard's return key: "next" on line 1 while line 2 is shown, otherwise "done". */
        firstInput?.setAttribute('enterkeyhint', two ? 'next' : 'done');
        secondInputEl?.setAttribute('enterkeyhint', 'done');
        secondLabel.textContent = two ? 'Remove second line' : `Add Another Line (+$${amount})`;
        secondIcon.classList.toggle('is-minus',two);
        if (!engravingOn() && !confirm.hidden) setConfirmed(false);
      };
      const done = document.createElement('button'); done.type = 'button';
      done.className = 'xz-v2-pdp-reset ff-cta ff-is-outline xz-v2-pdp-engraving-done';
      const doneLabel = document.createElement('span'); doneLabel.className = 'ff-button-3-txt'; doneLabel.textContent = 'DONE';done.append(doneLabel);
      content.append(second,done);
      const { signal } = this.abort;
      toggle.addEventListener('click', () => {
        /* Milen a-3/a-4 start sliding in the tap frame: flip the switch first, run the native
           engraving handlers (panel, fees, focus) just after that frame is painted. */
        const on = toggle.getAttribute('aria-checked') !== 'true';
        toggle.setAttribute('aria-checked', String(on));
        requestAnimationFrame(() => setTimeout(() => {
          if (!this.abort) return;
          /* Act on the state the shopper asked for, never on the hidden checkbox (a restored box made tap 1 a
             no-op). Both native handlers are idempotent: ON opens the panel and sets the engraving state, OFF
             clears it. */
          if (on) nativeAdd.click(); else nativeRemove.click();
          setConfirmed(false);
          sync();
        }, 0));
      }, { signal });
      /* "✓ ADDED" reopens the bar with the text kept (only #btn-no-engraving clears it); line 1 takes focus
         only when the confirmation was activated from the keyboard. From a tap or click the confirmation hides
         under the pointer, so focus goes to the switch beside it (no on-screen keyboard, no scroll) instead of
         falling to <body>. */
      confirm.addEventListener('click', (event) => {
        nativeAdd.click();
        setConfirmed(false);
        sync();
        if (event.detail === 0) container.querySelector('#engravedInput')?.focus();
        else { try { toggle.focus({ preventScroll: true }); } catch (error) { toggle.focus(); } }
      }, { signal });
      second.addEventListener('click', () => {
        if (nativeTwo.classList.contains('selected')) nativeOne.click(); else nativeTwo.click();
        sync();
      }, { signal });
      let doneByKeyboard = false;
      done.addEventListener('click', (event) => {
        doneByKeyboard = event.detail === 0;
        const first = container.querySelector('#engravedInput');
        const secondInput = container.querySelector('#engravedInput2');
        requestEngravingConfirmation(container, first, secondInput, nativeTwo.classList.contains('selected'), finish, this);
      }, { signal });
      /* Return in an engraving box (the phone's Go / return key, often pressed just to close the keyboard)
         never submits the product form: on line 1 with the second line shown it moves to line 2, otherwise it
         is DONE (the preview, or the empty-box message). A composing keyboard keeps its own Enter. */
      [firstInput, secondInputEl].forEach((input) => input?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.isComposing || event.keyCode === 229) return;
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        event.preventDefault();
        if (input === firstInput && nativeTwo.classList.contains('selected') && secondInputEl) { secondInputEl.focus(); return; }
        done.click();
      }, { signal }));
      /* The buy-button guard (xz-v2-ws-pdp.js) points a box at its message while it shows and may drop the whole
         attribute when it clears on input; put the rule and limit back after it has run. */
      [firstInput, secondInputEl].forEach((input) => input?.addEventListener('input', () => setTimeout(() => {
        if (this.abort && !(input.getAttribute('aria-describedby') || '').split(/\s+/).includes(limitHint.id)) describe(input, true);
      }, 0), { signal }));
      const setConfirmed = (on) => {
        confirm.hidden = !on;
        live.textContent = on ? 'Engraving added' : '';
      };
      /* DONE (directly, or after the TrueMark preview's confirm): the bar closes, the switch stays on (the
         engraving and its fee still apply) and "✓ ADDED" shows. Focus moves to it after a keyboard DONE, and
         whenever focus would otherwise be lost inside the closed bar (no scroll; a mouse press shows no ring). */
      const finish = () => {
        content.style.display = 'none';
        setConfirmed(true);
        const active = document.activeElement;
        if (doneByKeyboard || !active || active === document.body || content.contains(active)) {
          try { confirm.focus({ preventScroll: true }); } catch (error) { confirm.focus(); }
        }
        doneByKeyboard = false;
      };
      container.addEventListener('engraving:confirmed', finish, { signal });
      /* Back / forward (v2 pages are no-store, so Back re-parses the page and Chrome restores form controls; a
         back/forward-cache restore brings back the live DOM): the switch, the hidden checkbox, the panel,
         "✓ ADDED" and the window state the add reads are put back in agreement. Off unless the engraving is on;
         an engraving that is on but shows neither its panel nor "✓ ADDED" reopens its panel. */
      window.addEventListener('pageshow', () => {
        if (!this.abort) return;
        const selected = window.engravingSelected === true;
        if (!selected && (state.checked || content.style.display !== 'none' || !confirm.hidden)) nativeRemove.click();
        else if (selected && (!state.checked || (content.style.display === 'none' && confirm.hidden))) nativeAdd.click();
        if (!engravingOn()) setConfirmed(false);
        sync();
      }, { signal });
      this.engravingCleanup = () => {if (firstInput && originalPlaceholder !== null) firstInput.setAttribute('placeholder',originalPlaceholder);describe(firstInput,false);describe(secondInputEl,false);firstInput?.removeAttribute('enterkeyhint');secondInputEl?.removeAttribute('enterkeyhint');if (note) {note.innerHTML=noteOriginal;if (noteOriginalId) note.id=noteOriginalId; else note.removeAttribute('id');content.insertBefore(note,noteNext?.parentNode === content ? noteNext : null);}secondLabelEl.remove();limitHint.remove();row.remove();heading.remove();second.remove();done.remove();delete container.dataset.v2Enhanced;};
      container.dataset.v2Enhanced = '';
      sync();
    }
    /* ---------- gallery ----------
       Phones and tablets (991 and below): the panels are one native horizontal scroll-snap track, so the browser does
       the swipe (finger tracking, momentum, snap, swipe-or-scroll); this code only keeps state in step with where the
       track is: thumbnails (aria-pressed + gold frame), Milen's dots, the active panel and its zoom button's tab stop,
       neighbour preloads, and video/iframe reset once a swipe has settled. 992+: today's one panel at a time
       (.is-active), swapped after the chosen photo has decoded. Listeners are delegated on this element, so a gallery
       replaced by a variant change needs no rebinding. No rAF: scroll events come once per frame while it moves. */
    /* The track's slides, cached (the scroll handler runs every frame of a swipe); a replaced gallery (variant
       re-render) disconnects the cached track and an added or removed panel changes its child count, so the list
       rebuilds itself (both checks read no layout). */
    gallerySlides() {
      const cached = this.gTrackEl;
      if (cached && this.gSlideList && cached.isConnected && cached.childElementCount === this.gChildCount) return this.gSlideList;
      const track = this.gTrackEl = this.querySelector('[data-xz-track]');
      this.gChildCount = track ? track.childElementCount : 0;
      this.gSlideList = track ? Array.from(track.children).filter((node) => node.hasAttribute('data-media-id')) : [];
      return this.gSlideList;
    }
    galleryTrack() { this.gallerySlides(); return this.gTrackEl; }
    initGallery(signal) {
      const slides = this.gallerySlides();
      this.gIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.gTarget = null;
      this.gWidth = 0;
      this.gShown = slides[this.gIndex] || null;
      /* The track may already sit elsewhere (a swipe before this module ran; a restored page): follow it. Not while the
         gallery's own first-frame rAF (snippets/xz-v2-pdp-gallery.liquid, t.xzStart) is still to run: the track is
         then at 0 only because that rAF has not placed it yet, and pinTrack below places it instead. */
      const track = this.galleryTrack();
      if (track && track.xzStart != null) track.xzStart = null;
      else if (track && phoneQuery.matches && track.clientWidth) {
        const at = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
        if (at !== this.gIndex) this.setActive(at);
      }
      this.pinTrack();
      this.addEventListener('scroll', (event) => { if (event.target.hasAttribute?.('data-xz-track')) this.trackScrolled(event.target); }, { signal, capture: true, passive: true });
      this.addEventListener('scrollend', (event) => { if (event.target.hasAttribute?.('data-xz-track')) this.settleGallery(); }, { signal, capture: true });
      /* A finger on the track takes over from a thumbnail glide and warms the neighbours, two ahead (quick repeated
         swipes outran a one-ahead preload). */
      this.addEventListener('touchstart', (event) => {
        if (!event.target.closest?.('[data-xz-track]')) return;
        this.gTarget = null;
        this.warmSlide(this.gIndex - 1); this.warmAhead(this.gIndex, 1);
      }, { signal, capture: true, passive: true });
      /* The next photo starts as soon as the first photo has loaded (the gallery's inline script); once the page has
         loaded and is idle the one after it follows, and the viewer module downloads, so the first tap opens it
         without waiting on the network. */
      const afterLoad = () => idle(() => {
        if (!this.abort) return;
        if (phoneQuery.matches) { this.warmSlide(this.gIndex - 1); this.warmAhead(this.gIndex, 1); }
        this.loadViewer().catch(() => {});
      });
      if (document.readyState === 'complete') afterLoad(); else window.addEventListener('load', afterLoad, { signal, once: true });
      /* Pointer (mouse/pen) or touch landing on a photo: start the viewer download if it is not there yet. */
      this.addEventListener('pointerdown', (event) => { if (event.target.closest?.('[data-pdp-zoom]')) this.loadViewer().catch(() => {}); }, { signal, passive: true });
    }
    /* Same track width as last time = nothing to do; otherwise (rotation, a new gallery, 992 -> 991) put the current
       photo back in place. */
    pinTrack() {
      const track = this.galleryTrack();
      if (!track || !phoneQuery.matches) { this.gWidth = 0; return; }
      const width = track.clientWidth;
      if (!width || width === this.gWidth) return;
      this.gWidth = width;
      this.gTarget = null;
      const left = this.gIndex * width;
      if (Math.abs(track.scrollLeft - left) > 0.5) track.scrollLeft = left;
    }
    /* A variant re-render replaced the gallery with the server's copy (its .is-active panel is the variant's photo):
       position the new track in the same task, so the next frame already shows it. */
    adoptGallery() {
      this.mediaToken = (this.mediaToken || 0) + 1;
      this.gTrackEl = null; this.gSlideList = null;
      const slides = this.gallerySlides();
      this.gIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.gTarget = null;
      this.gWidth = 0;
      this.pinTrack();
      this.setActive(this.gIndex);
      this.gShown = slides[this.gIndex] || null;
    }
    trackScrolled(track) {
      if (!phoneQuery.matches) return;
      /* The width pinTrack keeps (it runs on every window resize, which comes before that frame's scroll events), not
         a clientWidth read per scroll event. */
      const width = this.gWidth || track.clientWidth;
      if (!width) return;
      if (this.gTarget != null) {
        /* A thumbnail tap glides across several photos: hold the chosen one until the track arrives. */
        if (Math.abs(track.scrollLeft - this.gTarget * width) > 2) { this.armSettle(); return; }
        this.gTarget = null;
      }
      const last = this.gallerySlides().length - 1;
      const i = Math.max(0, Math.min(last, Math.round(track.scrollLeft / width)));
      if (i !== this.gIndex) this.setActive(i, true);   // flips at the halfway point, as Instagram's dots do
      this.armSettle();
    }
    armSettle() {
      if ('onscrollend' in window) return;
      clearTimeout(this.gSettle);
      this.gSettle = setTimeout(() => this.settleGallery(), 150);
    }
    /* The swipe has come to rest: pause any video that is not on screen and reset the embed that was (today's
       showMedia rule, applied once the photo has settled rather than half way through a swipe). */
    settleGallery() {
      clearTimeout(this.gSettle); this.gSettle = 0;
      const slides = this.gallerySlides();
      if (this.gTarget != null) {
        this.gTarget = null;
        const track = this.galleryTrack();
        if (track && phoneQuery.matches && track.clientWidth) {
          const i = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
          if (i !== this.gIndex) this.setActive(i);
        }
      }
      const current = slides[this.gIndex];
      slides.forEach((slide) => { if (slide !== current) slide.querySelectorAll('video').forEach((video) => video.pause()); });
      if (this.gShown && this.gShown !== current && this.gShown.isConnected) this.gShown.querySelectorAll('iframe').forEach((frame) => { frame.src = frame.src; });
      this.gShown = current || null;
    }
    /* One place that publishes the active photo: thumbnails, dots, the panel class and its zoom button's tab stop,
       and the neighbours' downloads. */
    setActive(i, smooth) {
      const slides = this.gallerySlides();
      if (!slides[i]) return;
      const dir = i < this.gIndex ? -1 : 1;
      this.gIndex = i;
      this.markThumbs(slides[i].dataset.mediaId, smooth);
      this.panelState(slides, i);
      /* Two ahead in the direction of travel, one behind. */
      this.warmSlide(i); this.warmAhead(i, dir); this.warmSlide(i - dir);
    }
    panelState(slides, i) {
      slides.forEach((slide, k) => {
        const on = k === i;
        slide.classList.toggle('is-active', on);
        const zoom = slide.querySelector('[data-pdp-zoom]');
        if (zoom) { if (on) zoom.removeAttribute('tabindex'); else zoom.setAttribute('tabindex', '-1'); }
      });
    }
    /* Start a photo's download before it is needed (a lazy image inside a horizontal scroller otherwise starts only
       when it intersects, i.e. mid-swipe). */
    warmSlide(i) {
      const image = this.gallerySlides()[i]?.querySelector('img.ff-image-product');
      if (image && image.loading === 'lazy') image.loading = 'eager';
      return image || null;
    }
    /* The photo `dir` steps ahead, then the one after it once that has arrived: on a slow network the nearer photo
       gets the bandwidth first (two at once halved it on fast 3G). */
    warmAhead(i, dir) {
      const near = this.warmSlide(i + dir);
      const far = () => { if (this.abort) this.warmSlide(i + 2 * dir); };
      if (!near || (near.complete && near.naturalWidth)) far();
      else near.addEventListener('load', far, { once: true });
    }
    /* The image of a media panel; `eager` starts its download while the panel is still hidden. */
    mediaImage(id, eager) {
      const panel = this.gallerySlides().find((node) => node.dataset.mediaId === id);
      const image = panel?.querySelector('img.ff-image-product');
      if (image && eager && image.loading === 'lazy') image.loading = 'eager';
      return image;
    }
    /* Thumbnail tap, arrow keys, the viewer. Phones: the track glides there (jumps with reduced motion or `instant`,
       which the viewer uses under its sheet). 992+: the current photo stays until the chosen one has decoded (no black
       frame between photos on a slow network); the thumbnail state changes at once. */
    selectMedia(id, { instant = false, focus = false } = {}) {
      const slides = this.gallerySlides();
      const i = slides.findIndex((slide) => slide.dataset.mediaId === id);
      if (i < 0) return;
      const track = this.galleryTrack();
      if (phoneQuery.matches && track && track.clientWidth) {
        this.mediaToken = (this.mediaToken || 0) + 1;
        const left = i * track.clientWidth;
        const glide = !instant && canGlide() && Math.abs(track.scrollLeft - left) > 1;
        this.setActive(i, !instant);
        if (glide) {
          this.gTarget = i;
          track.scrollTo({ left, behavior: 'smooth' });
        } else {
          this.gTarget = null;
          if (Math.abs(track.scrollLeft - left) > 0.5) track.scrollLeft = left;
          this.settleGallery();
        }
        if (focus) focusQuiet(slides[i].querySelector('[data-pdp-zoom]'));
        return;
      }
      const token = this.mediaToken = (this.mediaToken || 0) + 1;
      const image = this.mediaImage(id, true);
      this.gIndex = i;
      this.markThumbs(id);
      const show = () => {
        if (this.mediaToken !== token || !this.abort) return;
        this.showMedia(id);
        /* The viewer's focus return waits for this photo's decode (returnFocus below), but only takes focus if it is
           still free: on <body> or in the gallery. A keyboard user who has tabbed on meanwhile keeps their place. */
        const late = this.focusAfterShow === id;
        if (late) this.focusAfterShow = null;
        if (focus || (late && this.focusIsFree())) focusQuiet(slides[i].querySelector('[data-pdp-zoom]'), !focus && late && this.focusAfterShowQuiet);
      };
      if (image && !(image.complete && image.naturalWidth) && image.decode) { image.decode().then(show, show); return; }
      show();
    }
    showMedia(id) {
      const slides = this.gallerySlides();
      const i = slides.findIndex((slide) => slide.dataset.mediaId === id);
      if (i < 0) return;
      slides.forEach((slide, k) => {
        if (k === i) return;
        slide.querySelectorAll('video').forEach((video) => video.pause());
        if (slide.classList.contains('is-active')) slide.querySelectorAll('iframe').forEach((frame) => { frame.src = frame.src; });
      });
      this.panelState(slides, i);
      this.gShown = slides[i];
    }
    focusIsFree() {
      const active = document.activeElement;
      return !active || active === document.body || active === document.documentElement || !!this.querySelector('[data-xz-v2-pdp-gallery]')?.contains(active);
    }
    markThumbs(id, smooth) {
      const thumbs = this.querySelectorAll('[data-pdp-media]');
      let chosen = null;
      thumbs.forEach((thumb) => { if (thumb.dataset.pdpMedia === id) chosen = thumb; });
      const reveal = this.railTarget(chosen);   // reads first, then the writes below
      thumbs.forEach((thumb) => {
        const selected = thumb === chosen;
        thumb.setAttribute('aria-pressed', String(selected));
        thumb.classList.toggle('ff-is-active', selected);
        thumb.querySelector('.xz-thumb-border')?.classList.toggle('is-active', selected);
      });
      const at = this.gallerySlides().findIndex((slide) => slide.dataset.mediaId === id);
      this.querySelectorAll('.xz-v2-pdp-dots .pagination-dot').forEach((dot, k) => dot.classList.toggle('active', k === at));
      this.scrollRail(reveal, smooth && phoneQuery.matches);
    }
    /* ---------- full-screen viewer (assets/xz-v2-pdp-viewer.js) ----------
       One list of photos, rendered once by Liquid: each zoom button carries the file's true pixels (data-w/data-h) and
       its full-size URL (data-full). The list is read when the viewer opens, so a variant re-render never leaves it
       stale; photos are matched by media id (a video panel is skipped by the viewer, never miscounted). */
    /* The viewer's module and its stylesheet (assets/xz-v2-pdp-viewer.css, data-viewer-css: viewer-only rules stay off
       the render-blocking head CSS) load together; neither is needed before a tap. A script-inserted stylesheet never
       blocks rendering. A failure is retried on the next tap. */
    loadViewer() {
      if (!this.viewerModule) {
        const src = this.dataset.viewerSrc, css = this.dataset.viewerCss;
        this.viewerModule = src ? Promise.all([import(src), css ? loadStylesheet(css) : null]).then(([module]) => module) : Promise.reject(new Error('xz-v2-pdp: no viewer source'));
        this.viewerModule.catch(() => { this.viewerModule = null; });
      }
      return this.viewerModule;
    }
    /* One viewer at a time: a second tap while the module is still on its way (a slow network gives the first tap no
       feedback) or while a viewer is open does nothing, so no second history entry is pushed. A viewer that is
       fading out is replaced (its sheet already lets taps through; the new one pushes after its step back). */
    openZoom(trigger, pointer) {
      if (!trigger.querySelector('img') || this.viewerOpening || (this.viewer && !this.viewer.dead && !this.viewer.closing)) return;
      this.viewerOpening = true;
      this.loadViewer().then((module) => {
        this.viewerOpening = false;
        if (this.abort && trigger.isConnected) this.showViewer(module, trigger, pointer);
      }, () => { this.viewerOpening = false; });
    }
    /* The viewer's same-URL history entry (Back closes it) can outlive it: a reload or a restored tab with the viewer
       open comes back on that entry with no viewer, and Forward after Back lands on it again. Either way the entry is
       dead: Back from it would change nothing on screen. After a reload the next Back that lands on the identical
       entry below it steps once more; Forward onto a dead entry steps straight back off it. The marker is left in
       place (no replaceState while the page loads: it could cost Chrome its saved scroll position on reload). */
    initViewerHistory(signal) {
      let dead = false;
      try { dead = !!(history.state && history.state.xzViewer); } catch (error) { /* no history access */ }
      window.addEventListener('popstate', (event) => {
        if (this.viewerOpening || (this.viewer && !this.viewer.dead)) return;   // a viewer owns history right now
        const state = event.state;
        if (state && state.xzViewer) { try { history.back(); } catch (error) {} return; }
        if (dead) { dead = false; try { history.back(); } catch (error) {} }
      }, { signal });
    }
    showViewer(module, trigger, pointer) {
      const items = [];
      let start = 0;
      this.gallerySlides().forEach((slide) => {
        const zoom = slide.querySelector('[data-pdp-zoom]');
        const image = zoom?.querySelector('img');
        if (!zoom || !image) return;
        if (zoom === trigger) start = items.length;
        items.push({ id: slide.dataset.mediaId, full: zoom.dataset.full || image.currentSrc || image.src, fixed: zoom.hasAttribute('data-fixed'),
          w: Number(zoom.dataset.w) || Number(image.getAttribute('width')) || 0, h: Number(zoom.dataset.h) || Number(image.getAttribute('height')) || 0, alt: image.alt || '' });
      });
      const zoomOf = (item) => this.gallerySlides().find((slide) => slide.dataset.mediaId === item?.id)?.querySelector('[data-pdp-zoom]');
      /* The gallery's photo frame: the viewer never opens a photo smaller than the page already shows it. */
      const box = trigger.getBoundingClientRect();
      this.viewer = module.openViewer({
        items, start, label: 'Product photos', pointer: !!pointer,
        frame: { w: box.width, h: box.height },
        openerSrc: trigger.querySelector('img')?.currentSrc || '',
        lock: { lockScroll, unlockScroll },
        /* The page gallery follows the viewer under its sheet, at once (no glide), so closing reveals the same photo. The
           viewer reports a page change once its strip has come to rest (not at the halfway point of every swipe), and
           onClose syncs the last photo before the sheet fades. */
        onChange: (index, item) => { if (this.abort && item) this.selectMedia(item.id, { instant: true }); },
        onClose: (index, item) => { if (this.abort && item) this.selectMedia(item.id, { instant: true }); },
        /* 992+: the photo's panel may still be waiting for its decode; it takes focus when it shows, if focus is still free. */
        returnFocus: (index, item, quiet) => {
          const zoom = zoomOf(item);
          if (zoom && zoom.getClientRects().length) return zoom;
          if (zoom) { this.focusAfterShow = item.id; this.focusAfterShowQuiet = !!quiet; return null; }
          return trigger.isConnected ? trigger : null;
        }
      });
    }
    rotate(details, open) {
      const icon = details.querySelector('.xz-pdp-dropdown-icon');
      const target = open ? 180 : 0;
      if (!icon || icon.dataset.target === String(target)) return;
      icon.dataset.target = String(target);
      this.animateMotion(icon, Number(icon.dataset.rotation || 0), target, 200, angle => {
        icon.style.transform = `rotate(${angle}deg)`;
        icon.dataset.rotation = angle;
      });
    }
    holdClock(node) {
      if (!this.motionClock || this.clockReleases.has(node)) return;
      this.clockReleases.set(node, this.motionClock.acquire());
    }
    releaseClock(node) {
      const release = this.clockReleases?.get(node);
      if (release) { release(); this.clockReleases.delete(node); }
    }
    animateMotion(node, from, to, duration, paint) {
      cancelAnimationFrame(this.frames.get(node));
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) { paint(to); this.frames.delete(node); this.releaseClock(node); return; }
      const start = this.motionClock ? this.motionClock.now() : performance.now();
      this.holdClock(node);
      const tick = now => {
        const p = optimizeFloat(Math.min(1, Math.max(0, (now - start) / duration)));
        paint(from + (to - from) * optimizeFloat(1 - (1 - p) ** 2));
        if (p < 1) this.frames.set(node, requestAnimationFrame(tick));
        else { this.frames.delete(node); this.releaseClock(node); }
      };
      this.frames.set(node, requestAnimationFrame(tick));
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.clockReleases?.forEach((release) => release()); this.clockReleases?.clear();
      this.engravingCleanup?.(); this.engravingCleanup = null;
      clearTimeout(this.gSettle);
      if (this.viewer && !this.viewer.dead) this.viewer.destroy(true);
      this.viewer = null;
      this.frames?.forEach((frame) => cancelAnimationFrame(frame));
      this.querySelectorAll('video').forEach((video) => video.pause());
    }
  }
  customElements.define('xz-v2-pdp', XinzuoPdp);
}
